/**
 * Telegram Notification Utility
 * 
 * Sends notifications to Telegram via the backend API endpoint.
 * 
 * @param {Object} event - Event data
 * @param {string} event.event - Event type ('wallet_connect', 'claim', etc.)
 * @param {string} event.chain - Chain name ('Solana', 'Ethereum', 'BNB Smart Chain', etc.)
 * @param {string} event.status - Status ('success' or 'failed')
 * @param {string} event.amount - Amount with symbol (e.g., '1.5 ETH', '7.9 SOL')
 * @param {string} event.account - Wallet address (will be truncated)
 * @param {string} [event.txHash] - Transaction hash (optional, will be truncated)
 * @param {string} [event.detail] - Additional detail/error message (optional)
 */

const truncate = (addr) => {
  if (!addr || typeof addr !== 'string' || addr === 'null' || addr === 'undefined') return 'n/a';
  // Don't truncate if already short or if it's 'n/a'
  if (addr === 'n/a' || addr.length <= 10) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
};

export async function notifyTelegram(event) {
  // Ensure event object exists
  if (!event || typeof event !== 'object') {
    console.warn('notifyTelegram: Invalid event object');
    return;
  }

  try {
    // Prepare payload with consistent formatting
    const payload = {
      event: event.event || 'unknown',
      chain: event.chain || 'unknown',
      status: event.status || 'unknown',
      amount: event.amount || 'n/a',
      account: truncate(event?.account),
      // Only include txHash if provided and not null
      ...(event.txHash && event.txHash !== 'null' && event.txHash !== 'undefined' 
        ? { txHash: truncate(event.txHash) } 
        : {}),
      // Only include detail if provided
      ...(event.detail && event.detail !== 'null' && event.detail !== 'undefined'
        ? { detail: event.detail }
        : {}),
    };

    // Send to backend API endpoint
    const response = await fetch('/api/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    // Check if response is ok (but don't throw - best effort only)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn('Telegram notification API error:', response.status, errorData);
    }
  } catch (err) {
    // Best-effort only; never block UI or throw errors
    console.warn('Telegram notification failed (non-blocking):', err.message);
  }
}

